import { inject, Injectable, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ToastService } from '@services/toast.service';

/**
 * Erro de validação retornado pela API.
 */
export interface ErroValidacao {
  campo: string;
  mensagem: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private readonly toastService = inject(ToastService);

  /**
   * Signal armazenando erros de validação da API.
   * Estrutura: { 'nomeDocampo': 'mensagem de erro' }
   */
  readonly errosValidacao = signal<Record<string, string>>({});

  /**
   * Valida o formulário apresentado localmente.
   *
   * @param form FormGroup a validar.
   * @returns true se válido, false caso contrário.
   */
  public validarFormulario(form: FormGroup): boolean {
    if (form.invalid) {
      const camposInvalidos = Object.keys(form.controls).filter(campo => form.get(campo)?.invalid);

      if (camposInvalidos.length > 0) {
        const camposInvalidosFormatados = camposInvalidos.join(', ');
        this.toastService.toastAviso(
          'Campos inválidos!',
          `Os seguintes campos estão inválidos: ${camposInvalidosFormatados}`,
        );
      } else {
        this.toastService.toastCamposInvalidos();
      }

      form.markAllAsTouched();
      form.markAsDirty();
      form.updateValueAndValidity();

      return false;
    }
    return true;
  }

  /**
   * Armazena erros de validação retornados pela API (erro 400).
   *
   * @param erros Array de objetos com campo e mensagem.
   */
  public definirErrosValidacao(erros: ErroValidacao[]): void {
    const mapaErros: Record<string, string> = {};
    erros.forEach((erro) => {
      // Normaliza o nome do campo para minúsculas (padrão camelCase do front)
      const campoNormalizado = erro.campo.charAt(0).toLowerCase() + erro.campo.slice(1);
      mapaErros[campoNormalizado] = erro.mensagem;
    });
    this.errosValidacao.set(mapaErros);
  }

  /**
   * Limpa todos os erros de validação armazenados.
   */
  public limparErrosValidacao(): void {
    this.errosValidacao.set({});
  }

  /**
   * Aplica erros de validação da API aos campos do formulário.
   *
   * Adiciona validador customizado aos campos com erro e os marca como touched/dirty.
   *
   * @param form FormGroup onde aplicar os erros.
   */
  public aplicarErrosValidacaoAoFormulario(form: FormGroup): void {
    const erros = this.errosValidacao();

    // Limpa apenas erro de API anterior sem interferir nos demais validadores/erros
    Object.keys(form.controls).forEach((campo) => {
      const control = form.get(campo);
      if (control) {
        const errosAtuais = control.errors ?? {};
        if ('apiError' in errosAtuais) {
          const novosErros = { ...errosAtuais };
          delete novosErros['apiError'];
          control.setErrors(Object.keys(novosErros).length > 0 ? novosErros : null);
        }
      }
    });

    // Aplica novos erros
    Object.entries(erros).forEach(([campo, mensagem]) => {
      const control = form.get(campo);
      if (control) {
        // Adiciona erro customizado sem sobrescrever validadores existentes
        control.setErrors({ ...control.errors, apiError: mensagem });
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

}
